import React, { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import QuipaAgentLogo from '../../assets/icons/quipa_q_logo.svg';
import { checkGate } from '../../statsig';
import styled from '../../theme';
import { FABRIC_URL_PARSER_GATE } from './fabric-url-feature-flag';
import { CategoryIds } from './fabric-url-prefill';
import AiFabricReady from '../../assets/icons/ai-fabric-check-mark.svg';
import { Button } from '../../../components/ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

export interface FabricUrlImportSubmitPayload {
  url: string;
  sectionName: string;
  categoryId: CategoryIds;
  pathId: string;
  pathName: string;
}

interface FabricUrlImportProps {
  sectionName: string;
  categoryId: CategoryIds;
  pathId: string;
  pathName: string;
  disabled?: boolean;
  orderType?: string;
  onSubmit?: (payload: FabricUrlImportSubmitPayload) => Promise<void>;
}

const steps = [
  'Accessing the website...',
  'Reading fabric details...',
  'Finding specifications...',
  'Preparing the component sheet...',
];

const STEP_ADVANCE_INTERVAL_MS = 4000;
const readyMessage = 'Details ready — review and save in the side sheet.';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: -14px 0 22px;
  padding: 16px;
  // border: 1px solid #b9ddec;
  border-radius: 7px;
  // background: #f0fbfe;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #202124;
  font-size: 14px;
  font-weight: 700;
`;

const NewPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ff6084;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
`;

const UrlEntry = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// const UrlInput = styled.input`
//   min-width: 0;
//   height: 40px;
//   padding: 0 12px;
//   border: 1px solid #d5dce1;
//   border-radius: 6px;
//   color: #1f2933;
//   font-size: 14px;

//   &:focus {
//     outline: none;
//     border-color: #338daa;
//     box-shadow: 0 0 0 2px rgba(51, 141, 170, 0.18);
//   }

//   &:disabled {
//     background: #f1f3f4;
//     color: #6b7280;
//     cursor: not-allowed;
//   }
// `;

// const SubmitButton = styled.button`
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   min-width: 112px;
//   height: 40px;
//   padding: 0 14px;
//   border: 0;
//   border-radius: 6px;
//   background: #338daa;
//   color: #ffffff;
//   font-size: 14px;
//   font-weight: 700;
//   cursor: pointer;

//   &:disabled {
//     background: #9db9c3;
//     cursor: not-allowed;
//   }
// `;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 34px;
  color: #338daa;
  font-size: 13px;
  font-weight: 700;

  &.fabric-url-status--error {
    color: #b42318;
  }
`;

const StatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
`;

const AgentMark = styled(QuipaAgentLogo)`
  width: 36px;
  height: 36px;
  color: #74c1d3;
  opacity: 0.42;
  animation: fabric-agent-logo-pulse 1.25s ease-in-out infinite;

  path {
    fill: currentColor !important;
  }

  @keyframes fabric-agent-logo-pulse {
    0%,
    100% {
      opacity: 0.34;
    }

    50% {
      opacity: 0.95;
    }
  }
`;

// const ReadyIcon = styled(CheckCircle2)`
//   width: 27px;
//   height: 27px;
//   stroke-width: 2.1;
// `;

const ErrorIcon = styled(AlertCircle)`
  width: 25px;
  height: 25px;
  stroke-width: 2.1;
`;

// const StatusMessage = styled.span`
//   // line-height: 18px;
// `;

const FabricUrlStatus = ({
  status,
  message,
}: {
  status: 'loading' | 'ready' | 'error';
  message: string;
}) => {
  const statusClassName = `fabric-url-status--${status} text-teal`;

  return (
    <Status className={statusClassName}>
      <StatusIcon aria-hidden="true">
        {status === 'loading' && <AgentMark />}
        {status === 'ready' && <AiFabricReady className="!w-[26px] !h-[26px]" />}
        {status === 'error' && <ErrorIcon />}
      </StatusIcon>
      <span className="font-medium">{message}</span>
    </Status>
  );
};

const getFabricUrlErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.message ||
  'Unable to fill fabric details from this URL.';

const validateFabricUrl = (value: string) => {
  try {
    const parsedUrl = new URL(value);
    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch (_error) {
    return false;
  }
};

const FabricUrlImport = ({
  sectionName,
  categoryId,
  pathId,
  pathName,
  disabled = false,
  onSubmit,
}: FabricUrlImportProps): JSX.Element | null => {
  const showFabricUrlParser = checkGate(FABRIC_URL_PARSER_GATE);
  const [url, setUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = useMemo(() => steps[Math.min(stepIndex, steps.length - 1)], [stepIndex]);

  useEffect(() => {
    if (!isSubmitting) {
      setStepIndex(0);
      return;
    }

    if (stepIndex >= steps.length - 1) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setStepIndex((current) => Math.min(current + 1, steps.length - 1));
    }, STEP_ADVANCE_INTERVAL_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isSubmitting, stepIndex]);

  if (!showFabricUrlParser || !onSubmit) {
    return null;
  }

  const submitFabricUrl = async () => {
    if (disabled || isSubmitting) {
      return;
    }

    const trimmedUrl = url.trim();

    if (!validateFabricUrl(trimmedUrl)) {
      setIsReady(false);
      setErrorMessage('Enter a valid fabric URL.');
      return;
    }

    try {
      setErrorMessage('');
      setIsReady(false);
      setIsSubmitting(true);
      await onSubmit({
        url: trimmedUrl,
        sectionName,
        categoryId,
        pathId,
        pathName,
      });
      setIsReady(true);
    } catch (error) {
      setIsReady(false);
      setErrorMessage(getFabricUrlErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUrlInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    void submitFabricUrl();
  };

  return (
    <Wrapper className="border border-teal/30 bg-teal/5">
      <Header>
        <NewPill>New</NewPill>
        <span>Enter fabric URL and let the QUIPA agent fill out details for you</span>
      </Header>
      <UrlEntry>
        <Input
          className={`${cn('disabled:pointer-events-auto')}`}
          type="url"
          value={url}
          placeholder="Enter Fabric URL"
          onChange={(event) => {
            setUrl(event.target.value);
            setIsReady(false);
            setErrorMessage('');
          }}
          onKeyDown={handleUrlInputKeyDown}
          disabled={disabled || isSubmitting}
          aria-label={`Fabric URL for ${sectionName}`}
        />
        {/* <UrlInput
          type="url"
          value={url}
          placeholder="Enter Fabric URL"
          onChange={(event) => {
            setUrl(event.target.value);
            setIsReady(false);
            setErrorMessage('');
          }}
          onKeyDown={handleUrlInputKeyDown}
          disabled={disabled || isSubmitting}
          aria-label={`Fabric URL for ${sectionName}`}
        /> */}
        <Button
          className="!h-auto"
          type="button"
          disabled={disabled || isSubmitting}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void submitFabricUrl();
          }}
        >
          {isSubmitting ? 'Filling...' : 'Submit URL'}
        </Button>
        {/* <SubmitButton
          type="button"
          disabled={disabled || isSubmitting}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void submitFabricUrl();
          }}
        >
          {isSubmitting ? 'Filling...' : 'Submit URL'}
        </SubmitButton> */}
      </UrlEntry>
      {isSubmitting && <FabricUrlStatus status="loading" message={currentStep} />}
      {!isSubmitting && isReady && <FabricUrlStatus status="ready" message={readyMessage} />}
      {!isSubmitting && errorMessage && <FabricUrlStatus status="error" message={errorMessage} />}
    </Wrapper>
  );
};

export default FabricUrlImport;
